import { Component, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { NumericUpDownCustomization } from './numericupdown-edit';

@Component({
  selector: 'lib-numericupdown-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      header="Personalizzazione Casella Numerica"
      [form]="customizationForm"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div [formGroup]="customizationForm" class="flex flex-col gap-2">
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
            ></textarea>
          </div>
          <div class="ml-0 mr-0 mt-0 grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1">
              <!-- <label for="min">Minimo</label> -->
              <div class="flex flex-row gap-2">
                <p-checkbox
                  [binary]="true"
                  label="Minimo"
                  formControlName="hasMin"
                />

                <label for="min">Minimo</label>
              </div>
              <p-inputNumber
                [autofocus]="false"
                id="min"
                [showButtons]="true"
                formControlName="minValue"
                decrementButtonClass="bg-primary"
                incrementButtonClass="bg-primary"
                class="p-fluid"
              />
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex flex-row gap-2">
                <p-checkbox id="max" [binary]="true" formControlName="hasMax" />
                <label for="max">Massimo</label>
              </div>
              <p-inputNumber
                [autofocus]="false"
                id="max"
                [showButtons]="true"
                formControlName="maxValue"
                decrementButtonClass="bg-primary"
                incrementButtonClass="bg-primary"
                class="p-fluid"
              />
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex flex-row gap-2">
                <p-checkbox
                  [binary]="true"
                  label="Passo"
                  formControlName="hasStep"
                />
                <label for="step">Passo</label>
              </div>
              <p-inputNumber
                [autofocus]="false"
                id="step"
                [showButtons]="true"
                formControlName="stepValue"
                decrementButtonClass="bg-primary"
                incrementButtonClass="bg-primary"
                class="p-fluid"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label for="desc">Valore predefinito</label>
              <p-inputNumber
                [autofocus]="false"
                id="desc"
                [showButtons]="true"
                formControlName="defaultValue"
                decrementButtonClass="bg-primary"
                incrementButtonClass="bg-primary"
                class="p-fluid"
              />
            </div>
          </div>

          <div class="flex w-full flex-row">
            <label for="required" class="w-full self-start">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
              class="self-end"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    CheckboxModule,
    InputNumberModule,
    SelectModule,
    TextareaModule,
  ],
})
export class NumericUpDownCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<NumericUpDownCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  protected customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    defaultValue: [0],
    required: [false],
    hasMin: [false],
    hasMax: [false],
    hasStep: [false],
    minValue: [0],
    maxValue: [0],
    stepValue: [1],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    // topology: [0],
  });

  topologies = [
    {
      value: 0,
      viewValue: 'Normale',
    },
    {
      value: 1,
      viewValue: 'Con bottoni',
    },
  ];
  selectedTopology = 0;
  context = {};

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      const uniqueId = this.widgetService.uniqueId;
      if (!uniqueId) return;

      this.customizationForm.patchValue({
        id: uniqueId,
        description: widget.customization.description,
        defaultValue: widget.customization.defaultValue,
        required: widget.customization.required || false,
        hasMin: widget.customization.minValue !== undefined,
        minValue: widget.customization.minValue,
        hasMax: widget.customization.maxValue !== undefined,
        maxValue: widget.customization.maxValue,
        hasStep: widget.customization.stepValue !== undefined,
        stepValue: widget.customization.stepValue,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
        // topology: widget.customization['topology'] || 0,
      });
    });

    this.customizationForm.valueChanges.subscribe((v) => {
      console.log(v);
    });

    this.customizationForm.controls['hasMax'].valueChanges.subscribe((v) => {
      // console.log(v);
      if (!v) {
        this.customizationForm.controls.maxValue.disable();
      }
      if (v) {
        this.customizationForm.controls.maxValue.enable();
        this.customizationForm.controls.maxValue.setValue(0);
      }
    });
    this.customizationForm.controls['hasMin'].valueChanges.subscribe((v) => {
      if (!v) {
        this.customizationForm.controls.minValue.disable();
      }
      if (v) {
        this.customizationForm.controls.minValue.enable();
        this.customizationForm.controls.minValue.setValue(0);
      }
    });
    this.customizationForm.controls['hasStep'].valueChanges.subscribe((v) => {
      if (!v) {
        this.customizationForm.controls.stepValue.disable();
      }
      if (v) {
        this.customizationForm.controls.stepValue.enable();
        this.customizationForm.controls.stepValue.setValue(1);
      }
    });
  }

  updateWidget(event: boolean) {
    if (!event) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    console.log(formValue);

    if (formValue.hasMin === false) {
      formValue.minValue = undefined;
    }
    delete formValue.hasMin;

    if (formValue.hasMax === false) {
      formValue.maxValue = undefined;
    }
    delete formValue.hasMax;

    if (formValue.hasStep === false) {
      formValue.stepValue = undefined;
    }
    delete formValue.hasStep;

    const id = this.widgetService.uniqueId;
    if (!id) return;

    delete formValue.id;

    this.widgetService.updateWidget(id, formValue);

    this.widgetService.settingVisibility.set(false);
  }
}
